/**
 * WizardLayout — a step-by-step flow whose Back / Next bar is pinned to the
 * bottom of the window.
 *
 * Only the body above the bar scrolls, so:
 *   - Back and Next sit in the same place on every step, however long or short
 *     the step is (no hunting for Next below a long table);
 *   - a short step never leaves blank page to scroll into, and the scrollbar
 *     ends at the bar instead of running behind it;
 *   - a new step starts at its top, before it paints (a layout effect), so the
 *     last step's scroll position never flashes up as an empty screen.
 *
 * It fills its parent's height: the app shell's `<main>` (a page), a flex
 * column (a full-screen page with its own header), or a Sheet panel. The page
 * then scrolls this body instead of `<main>`, still one scroller.
 *
 * The bar's content comes either from `footer` (when the page owns the step
 * state) or from a `<WizardFooter>` anywhere below the layout (when each step
 * renders its own buttons); a WizardFooter outside a layout renders in place,
 * as before. The bar hides itself when a step shows no buttons.
 */

import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "./cn";

interface WizardLayoutContextValue {
  /** The pinned bar a WizardFooter renders into (null until it mounts). */
  slot: HTMLElement | null;
  /** Scroll the body back to its top. */
  scrollToTop: () => void;
}

const WizardLayoutContext = createContext<WizardLayoutContextValue | null>(null);

export interface WizardLayoutProps {
  children: ReactNode;
  /** The pinned bar's content: Back / Next and anything that rides with them. */
  footer?: ReactNode;
  /**
   * The current step. When it changes the body returns to its top. Leave it
   * out when the step lives further down, and call `useWizardStep` there.
   */
  stepKey?: unknown;
  className?: string;
  /** Extra classes for the scrolling body. */
  bodyClassName?: string;
}

export function WizardLayout({ children, footer, stepKey, className, bodyClassName }: WizardLayoutProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);

  const scrollToTop = useCallback(() => {
    const body = bodyRef.current;
    if (body) body.scrollTop = 0;
  }, []);
  useLayoutEffect(scrollToTop, [stepKey, scrollToTop]);

  const ctx = useMemo<WizardLayoutContextValue>(() => ({ slot, scrollToTop }), [slot, scrollToTop]);

  return (
    <WizardLayoutContext.Provider value={ctx}>
      <div className={cn("flex h-full min-h-0 flex-1 flex-col", className)}>
        <div
          ref={bodyRef}
          data-wizard-body
          // A reserved scrollbar gutter: a step that starts to scroll doesn't shift sideways.
          className={cn("min-h-0 flex-1 overflow-y-auto overscroll-contain [scrollbar-gutter:stable]", bodyClassName)}
        >
          {children}
        </div>
        <div
          ref={setSlot}
          data-wizard-footer
          className="flex-none border-t border-border bg-surface empty:hidden"
        >
          {footer}
        </div>
      </div>
    </WizardLayoutContext.Provider>
  );
}

/**
 * Back / Next for a step that renders its own buttons: inside a WizardLayout
 * they move to the pinned bar; anywhere else they render here, unchanged.
 * `className` styles the row either way (spacing, justify, max width).
 */
export function WizardFooter({ children, className }: { children: ReactNode; className?: string }) {
  const ctx = useContext(WizardLayoutContext);
  const row = <div className={className}>{children}</div>;
  return ctx?.slot ? createPortal(row, ctx.slot) : row;
}

/**
 * For a step kept below the WizardLayout (so the layout can't take it as
 * `stepKey`): a new step starts at the top of the body.
 */
export function useWizardStep(step: unknown) {
  const ctx = useContext(WizardLayoutContext);
  const scrollToTop = ctx?.scrollToTop;
  useLayoutEffect(() => {
    scrollToTop?.();
  }, [step, scrollToTop]);
}
